import { Article } from '../types';

export const applyPrepaidMeters2026: Article = {
    id: 'apply-prepaid-meters-2026',
    title: 'Step-by-Step Guide: How to Apply for Prepaid Electricity Meters in 2026',
    slug: 'prepaid-meter-application-nigeria-guide',
    excerpt: 'Navigating the MAP application process: How to get a prepaid electricity meter from your DisCo in Nigeria.',
    category: 'Power Generation',
    date: 'February 25, 2026',
    dateModified: 'June 8, 2026',
    readTime: '3 min read',
    author: 'power.ng Research',
    trending: false,
    keywords: ['prepaid meter application', 'MAP meter Nigeria', 'estimated billing', 'EKEDC meter', 'buy prepaid meter'],
    metaDescription: 'Eliminate estimated billing. Follow our 2026 step-by-step guide to legally applying and paying for a prepaid electricity meter via the MAP scheme in Nigeria.',
    faqs: [
        {
            question: 'How do I apply for a prepaid meter in Nigeria?',
            answer: 'Apply through your DisCo official MAP portal, complete KYC, allow premise inspection, pay only the official generated bank account or payment link, and keep your application reference number.',
        },
        {
            question: 'How long should prepaid meter installation take after payment?',
            answer: 'Under the MAP process, installation is generally expected within 10 working days after payment confirmation, though actual timing can vary by DisCo and MAP vendor.',
        },
        {
            question: 'Should I pay cash to a meter installer?',
            answer: 'No. Do not pay cash to staff, agents, or installers. Payments should go through official DisCo/MAP payment channels only.',
        },
    ],
    content: `## Quick Answer: How to Get a Prepaid Meter
To apply for a prepaid meter in Nigeria, avoid third-party "agents." Visit your specific Distribution Company's (DisCo) website, locate the **MAP (Meter Asset Provider)** portal, fill out the KYC form with your current account number, undergo the premise inspection, and pay the approved NERC fee directly to the designated bank account. Your meter will be installed within 10 working days of payment.

---

## Why You Need a Prepaid Meter Now

The removal of the cap on estimated billing for certain unmetered customers means that without a meter, you are at the mercy of arbitrary billing algorithms. Securing a prepaid meter guarantees you only pay for the electricity you physically consume. 

Here is the straightforward process to legally obtain a meter under the 2026 regulatory framework.

---

## Step-by-Step Application Process

### Step 1: Gather Your Information
Before starting, ensure you have:
* A copy of your recent electricity bill showing your **Account Number**.
* A valid government-issued ID (NIN, Driver's License, or International Passport).
* A clear passport photograph.
* Proof of property ownership or an authorization letter from your landlord.

### Step 2: Initiate Application on Your DisCo Portal
Navigate to your DisCo's official site (e.g., *map.ikejaelectric.com* for IKEDC or *ekedp.com* for EKEDC).
* Click on 'Apply for Meter' or the 'MAP Portal' link.
* Enter your Account Number for validation. If you have outstanding debts, you usually must agree to clear them or have them rolled over into your new prepaid account before proceeding.

### Step 3: Complete KYC 
Fill in the "Know Your Customer" details, upload the required documents, and submit the application. You will receive an **Application Reference Number (ARN)**.

### Step 4: Premise Inspection
Within 3-5 days of your application, the DisCo or a designated MAP vendor will send a technician to inspect your property. This is to verify your grid load, assess the electrical wiring safety, and determine if you need a Single Phase or Three Phase meter.

### Step 5: Make Your Payment
Once your premise passes inspection, you will receive an SMS and email notification with a payment link or designated bank account.
> [!CAUTION]
> **Anti-Fraud Warning:** Never pay cash to meter installers or staff. All payments must be made strictly to the bank accounts provided on the official DisCo web portal.

**2026 NERC Approved Meter Pricing (Approximate):**
* **Single Phase Meter:** ₦130,000 – ₦145,000
* **Three Phase Meter:** ₦220,000 – ₦250,000
*(Prices are exclusive of VAT and slight variations depend on the specific MAP vendor).*

### Step 6: Installation
Upon confirmation of payment, the Meter Asset Provider is legally obligated by NERC to install your prepaid meter within **10 working days**.

---

## Dealing with "Old Debts"
If you have historic estimated bills on your account, applying for a meter does not erase them. However, NERC mandates that DisCos must not use outstanding debt to deny you a meter. Instead, the debt will be migrated to your new prepaid meter, and a small percentage (e.g., 20%) of your future recharges will be automatically deducted to slowly pay off the balance.`
  };
